import { useCallback, useMemo, useState } from 'react';
import { MenuItemsProps } from '../../../components/MenuButton/MenuButton';
import useChimePlayer from '../../../components/KlydoPlayer/hooks/useChimePlayer';
import { SoundMode } from '../../../api/klydo';
import { hasSound } from '../../../utils';

const usePublicKlydoView = (
  soundMode?: SoundMode,
  hasChimes?: boolean,
  chimeUrl?: string,
) => {
  const [isMuted, setIsMuted] = useState<boolean>(true);
  const [replaySoundTrigger, setReplaySoundTrigger] = useState<number>(0);
  const { isPlaying: playChime, controller: chimeController } =
    useChimePlayer(chimeUrl);

  const playSoundOnce = soundMode === SoundMode.FIRST_LOOP;

  const handleMuteToggle = useCallback(() => {
    setIsMuted((prev: boolean) => !prev);
  }, []);

  const handleReplaySound = useCallback(() => {
    setReplaySoundTrigger((prev: number) => prev + 1);
  }, []);

  const handleChimeToggle = useCallback(() => {
    chimeController();
  }, [chimeController]);

  const menuButtonItemsList = useMemo(() => {
    const items: MenuItemsProps[] = [];

    if (hasSound(soundMode)) {
      items.push({
        title: isMuted ? 'Unmute Video' : 'Mute Video',
        isDisabled: false,
        onClick: handleMuteToggle,
      });
    }

    if (playSoundOnce && !isMuted) {
      items.push({
        title: 'Replay Sound',
        isDisabled: false,
        onClick: handleReplaySound,
      });
    }

    if (hasChimes) {
      items.push({
        title: playChime ? 'Stop Chime' : 'Play Chime',
        isDisabled: false,
        onClick: handleChimeToggle,
      });
    }

    return items.length > 0 ? items : undefined;
  }, [
    soundMode,
    playSoundOnce,
    hasChimes,
    isMuted,
    playChime,
    handleMuteToggle,
    handleReplaySound,
    handleChimeToggle,
  ]);

  return {
    menuButtonItemsList,
    isMuted,
    playSoundOnce,
    replaySoundTrigger,
    handleMuteToggle,
    playChime,
    handleChimeToggle,
  };
};

export default usePublicKlydoView;
